/**
 * KeypairWizardScreen — first-run setup for the browser-side decryption
 * key. Replaces the office-agent enrollment flow.
 *
 * Two visible states:
 *
 *   1. Enroll: user has no keypair on file. Pick a passphrase, derive a
 *      32-byte secret, generate (O_pk, O_sk), wrap O_sk to the secret,
 *      ship (O_pk, wrappedBlob) to the server. The wizard then sits
 *      waiting until the operator runs `dovach-op sign --account-id ...`
 *      and the cert is published.
 *   2. Unlock: a keypair is on file but the tab doesn't have O_sk in
 *      memory yet (fresh page load / different tab). Type passphrase →
 *      re-derive secret → unwrap → store in session.
 *
 * After unlock, the screen also walks the user through picking the drop
 * directory if none is saved.
 */

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import {
  base64,
  bytesToHex,
  derivePassphraseSecret,
  encodePassphrase,
  freshSalt,
  generateKeypair,
  prependSalt,
  splitSaltAndWrapped,
  unwrapSecretKey,
  wrapSecretKey,
  zero,
} from "@dovach/shared-crypto";

import { ApiError, api } from "../lib/api";
import {
  ensureDropDirAccess,
  pickDropDirectory,
} from "../lib/drop-dir";
import { setOsk } from "../lib/session-key";

type Phase = "loading" | "enroll" | "unlock" | "wait_for_cert" | "drop_dir" | "ready";

export function KeypairWizardScreen({ onReady }: { onReady: () => void }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    try {
      const status = await api.keypairStatus();
      if (!status.enrolled) {
        setPhase("enroll");
        return;
      }
      if (!status.signed) {
        setPhase("wait_for_cert");
        return;
      }
      // Enrolled + signed. Need an unlocked O_sk + a drop directory.
      // sessionStorage check is in lib/session-key, but we also need
      // the user to enter the passphrase to unlock if not yet done.
      const dirHandle = await ensureDropDirAccess();
      if (dirHandle === null) {
        setPhase("drop_dir");
        return;
      }
      setPhase("unlock");
    } catch {
      setError("בדיקת המצב נכשלה.");
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  if (error) return <Text c="red">{error}</Text>;

  if (phase === "loading") return <Text c="dimmed">טוען…</Text>;
  if (phase === "enroll")
    return (
      <EnrollPhase
        onDone={(already_signed) => {
          // Server-side auto-sign happened in the enroll response —
          // skip the wait_for_cert polling phase and re-check status
          // straight away (drop-dir / unlock branches).
          if (already_signed) {
            checkStatus();
          } else {
            setPhase("wait_for_cert");
          }
        }}
      />
    );
  if (phase === "wait_for_cert") return <WaitForCertPhase onSigned={checkStatus} />;
  if (phase === "drop_dir")
    return <DropDirPhase onDone={() => setPhase("unlock")} />;
  if (phase === "unlock")
    return (
      <UnlockPhase
        onUnlocked={async () => {
          // After unlock, ensure drop dir too.
          const dir = await ensureDropDirAccess();
          if (dir === null) {
            setPhase("drop_dir");
          } else {
            setPhase("ready");
            onReady();
          }
        }}
      />
    );
  return null;
}

/** Enroll: pick passphrase → derive secret → generate keypair → wrap → ship.
 * `onDone(already_signed)` true means the API auto-signed in the response. */
function EnrollPhase({ onDone }: { onDone: (already_signed: boolean) => void }) {
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (pass.length < 12) {
      setError("הסיסמה חייבת להיות באורך 12 תווים לפחות.");
      return;
    }
    if (pass !== pass2) {
      setError("הסיסמאות אינן תואמות.");
      return;
    }
    setBusy(true);
    try {
      const kp = generateKeypair();
      const salt = freshSalt();
      const secret = await derivePassphraseSecret(encodePassphrase(pass), salt);
      const wrapped = await wrapSecretKey(kp.secretKeyBytes, secret);
      const bundle = prependSalt(salt, wrapped);
      // Post-MVP auto-sign: when the API has SR_sk loaded, the enroll
      // response already contains a cert_blob. Pass that fact up so the
      // wizard skips the "wait for cert" polling phase entirely.
      const resp = await api.keypairEnroll(kp.publicKeyHex, base64.encode(bundle));
      // Stash O_sk in session so unlock isn't needed until next tab open.
      setOsk(kp.secretKeyBytes);
      // Defensively zero working buffers.
      zero(kp.secretKeyBytes);
      zero(secret);
      onDone(resp.cert_blob !== null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`שגיאה: ${err.detail}`);
      } else {
        setError("רישום המפתח נכשל.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack>
        <Title order={2}>הגדרת מפתח הצפנה</Title>
        <Text c="dimmed" size="sm">
          קבצים שלקוחות שולחים לרו"ח שלך מוצפנים. בחר סיסמה ארוכה (לפחות
          12 תווים) — נפיק ממנה את מפתח הפענוח. הסיסמה לא נשלחת לשרת.
          אם תשכח אותה, תוכל להשתמש בקוד שחזור או להתחיל מחדש (קבצים
          ישנים יישארו מוצפנים בענן עד אישור מחדש על ידי המפעיל).
        </Text>
        <PasswordInput
          label="סיסמה (חדשה, ייעודית למפתח ההצפנה)"
          value={pass}
          onChange={(e) => setPass(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
          required
        />
        <PasswordInput
          label="אישור סיסמה"
          value={pass2}
          onChange={(e) => setPass2(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
          required
        />
        {error && <Text c="red">{error}</Text>}
        <Group>
          <Button
            onClick={submit}
            loading={busy}
            disabled={pass.length < 12 || pass !== pass2}
          >
            צור מפתח
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Wait-for-cert: keypair posted, waiting for operator to sign. */
function WaitForCertPhase({ onSigned }: { onSigned: () => void }) {
  const [pk, setPk] = useState<string>("");
  const [accountId, setAccountId] = useState<string>("");

  useEffect(() => {
    Promise.all([api.keypair(), api.me()]).then(([r, me]) => {
      setPk(r.office_public_key ?? "");
      setAccountId(me.account_id);
    });
    const i = window.setInterval(async () => {
      const s = await api.keypairStatus();
      if (s.signed) {
        onSigned();
      }
    }, 5000);
    return () => window.clearInterval(i);
  }, [onSigned]);

  // Build a paste-and-run CLI command for the v=2 cert (post-Wave-7).
  // The cert binds (account_id, O_pk) only — no separate OA key.
  const cliCmd =
    pk && accountId
      ? `dovach-op sign --account-id ${accountId.replace(/-/g, "")} ` +
        `--office-pk ${pk}`
      : "";

  return (
    <Card withBorder padding="lg" maw={640}>
      <Stack>
        <Group>
          <Title order={2}>ממתין לחתימת המפתח</Title>
          <Badge color="yellow">בהמתנה</Badge>
        </Group>
        <Text size="sm">
          המפתח הציבורי שלך נשלח. כעת מפעיל המערכת צריך לחתום עליו במחשב
          הבלתי־מקוון שלו. העתק את הפקודה הבאה ושלח אותה למפעיל:
        </Text>
        {cliCmd && (
          <>
            <Code block dir="ltr" style={{ wordBreak: "break-all" }}>
              {cliCmd}
            </Code>
            <Group>
              <CopyButton value={cliCmd}>
                {({ copied, copy }) => (
                  <Button
                    variant={copied ? "filled" : "light"}
                    color={copied ? "green" : "blue"}
                    onClick={copy}
                  >
                    {copied ? "הועתק" : "העתק פקודה"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </>
        )}
        <Alert color="blue">
          הדף יתעדכן אוטומטית ברגע שהחתימה תפורסם. בדרך כלל פחות מדקה.
        </Alert>
      </Stack>
    </Card>
  );
}

/** Drop-dir: pick a folder for plaintext output. */
function DropDirPhase({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async () => {
    setBusy(true);
    setError(null);
    try {
      await pickDropDirectory();
      onDone();
    } catch (err) {
      const msg = (err as Error)?.message ?? "";
      if (msg === "FS_ACCESS_UNSUPPORTED") {
        setError(
          "הדפדפן שלך לא תומך ב־File System Access API. נסה Chrome או Edge.",
        );
      } else if (msg.includes("aborted")) {
        setError("בחירת תיקייה בוטלה.");
      } else {
        setError("בחירת התיקייה נכשלה.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack>
        <Title order={2}>בחר תיקיית יעד</Title>
        <Text c="dimmed" size="sm">
          קבצים מפוענחים ייכתבו לתיקייה שתבחר כעת. הדפדפן יזכור את הבחירה
          בין הפעלות. תוכל לשנות אותה מאוחר יותר ב־<Code>/settings/agent</Code>.
        </Text>
        {error && <Text c="red">{error}</Text>}
        <Group>
          <Button onClick={pick} loading={busy}>
            בחר תיקייה
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Unlock: enrolled + signed, but O_sk isn't in session memory yet. */
function UnlockPhase({ onUnlocked }: { onUnlocked: () => void }) {
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const stored = await api.keypair();
      if (!stored.wrapped_o_sk_b64) {
        setError("לא נמצא מפתח שמור — דרושה הרשמה מחדש.");
        return;
      }
      const bundle = base64.decode(stored.wrapped_o_sk_b64);
      const split = splitSaltAndWrapped(bundle);
      const secret = await derivePassphraseSecret(
        encodePassphrase(pass),
        split.salt,
      );
      const sk = await unwrapSecretKey(split.wrappedBlob, secret);
      setOsk(sk);
      zero(secret);
      onUnlocked();
    } catch (err) {
      const msg = (err as Error)?.message ?? "";
      if (msg.includes("AES-GCM") || msg.includes("OperationError")) {
        setError("סיסמה שגויה.");
      } else {
        setError("פתיחת המפתח נכשלה.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack>
        <Title order={2}>פתיחת מפתח</Title>
        <Text c="dimmed" size="sm">
          הזן את הסיסמה שבחרת בעת הגדרת מפתח ההצפנה. הסיסמה לא נשלחת
          לשרת.
        </Text>
        <PasswordInput
          label="סיסמה"
          value={pass}
          onChange={(e) => setPass(e.currentTarget.value)}
          autoComplete="current-password"
          dir="ltr"
          required
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && pass.length >= 12) submit();
          }}
        />
        {error && <Text c="red">{error}</Text>}
        <Group justify="space-between">
          <Anchor size="sm" href="#">
            איבדת סיסמה? פנה למפעיל
          </Anchor>
          <Button onClick={submit} loading={busy} disabled={pass.length < 12}>
            פתח
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
